import { cn } from '@/lib/utils'
import { Check, Package, Truck, Home, X } from 'lucide-react'
import { format } from 'date-fns'
import type { FulfillmentStatus } from '@/types/database'

interface OrderStatusTrackerProps {
  status: FulfillmentStatus
  createdAt: string
  shippedAt?: string | null
  deliveredAt?: string | null
}

interface StatusStep {
  key: FulfillmentStatus | 'ordered'
  label: string
  icon: React.ComponentType<{ className?: string }>
}

const steps: StatusStep[] = [
  { key: 'ordered', label: 'Ordered', icon: Check },
  { key: 'processing', label: 'Processing', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: Home },
]

const statusIndex: Record<FulfillmentStatus | 'ordered', number> = {
  ordered: 0,
  pending: 0,
  processing: 1,
  shipped: 2,
  delivered: 3,
  cancelled: -1,
}

export function OrderStatusTracker({
  status,
  createdAt,
  shippedAt,
  deliveredAt,
}: OrderStatusTrackerProps) {
  // Handle cancelled orders specially
  if (status === 'cancelled') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <X className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="font-medium text-red-800">Order Cancelled</p>
            <p className="text-sm text-red-600">
              This order has been cancelled
            </p>
          </div>
        </div>
      </div>
    )
  }

  const currentIndex = statusIndex[status] ?? statusIndex.pending

  // Get dates for each step
  const getStepDate = (stepKey: FulfillmentStatus | 'ordered'): string | null => {
    switch (stepKey) {
      case 'ordered':
      case 'pending':
        return createdAt
      case 'processing':
        return currentIndex >= 1 ? createdAt : null // Approximate
      case 'shipped':
        return shippedAt || null
      case 'delivered':
        return deliveredAt || null
      default:
        return null
    }
  }

  return (
    <div className="py-4">
      {/* Desktop/Tablet View */}
      <div className="hidden sm:block">
        <div className="flex items-start justify-between">
          {steps.map((step, index) => {
            const isCompleted = index <= currentIndex
            const isCurrent = index === currentIndex
            const stepDate = getStepDate(step.key)
            const Icon = step.icon

            return (
              <div key={step.key} className="flex flex-col items-center flex-1">
                {/* Step indicator */}
                <div className="flex items-center w-full">
                  {/* Line before */}
                  {index > 0 && (
                    <div
                      className={cn(
                        'h-0.5 flex-1',
                        index <= currentIndex ? 'bg-primary' : 'bg-muted'
                      )}
                    />
                  )}

                  {/* Circle */}
                  <div
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                      isCompleted
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-muted bg-background text-muted-foreground'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </div>

                  {/* Line after */}
                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        'h-0.5 flex-1',
                        index < currentIndex ? 'bg-primary' : 'bg-muted'
                      )}
                    />
                  )}
                </div>

                {/* Label */}
                <div className="mt-2 text-center">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      isCurrent ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {step.label}
                  </p>
                  {stepDate && isCompleted && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {format(new Date(stepDate), 'MMM d')}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Mobile View */}
      <div className="sm:hidden space-y-3">
        {steps.map((step, index) => {
          const isCompleted = index <= currentIndex
          const isCurrent = index === currentIndex
          const stepDate = getStepDate(step.key)
          const Icon = step.icon

          return (
            <div
              key={step.key}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg',
                isCurrent ? 'bg-primary/10' : isCompleted ? 'bg-muted/50' : ''
              )}
            >
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full',
                  isCompleted
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <p
                  className={cn(
                    'text-sm font-medium',
                    isCurrent ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {step.label}
                </p>
                {stepDate && isCompleted && (
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(stepDate), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
              {isCompleted && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
